import { gql } from "@apollo/client";

// ============================================================================
// SEARCH QUERIES
// ============================================================================

import {
  SITE_FIELDS,
  GENERAL_FIELDS,
  PAGE_SETTINGS,
  ARTICLE_FIELDS,
  OFFICE_LOCATION,
} from "./shared";
import { ARTICLE_TEASER_IMAGE } from "./articles";
import { OFFICE_FIELDS, OFFICE_INTRO_TEASER_IMAGE } from "./offices";

export const SEARCH_QUERY = gql`
  ${SITE_FIELDS}
  ${GENERAL_FIELDS}
  ${PAGE_SETTINGS}
  query GetSearchPage($locale: SiteLocale!) {
    site: _site(locale: $locale) {
      ...SiteFields
    }
    general(locale: $locale) {
      ...GeneralFields
    }
    fields: search(locale: $locale) {
      # Search Meta (SEO)
      slugs: _allSlugLocales {
        locale
        value
      }
      titles: _allTitleLocales {
        locale
        value
      }
      seoMetaTags: _seoMetaTags {
        attributes
        content
        tag
      }

      # Search Fields (Content)
      id
      slug
      title
      shortTitle
      intro {
        value
      }

      # Search Intro Image
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "1560", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
      introImageMobile: introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "525", h: "375", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }

      # Search Components
      pageSettings {
        ...PageSettings
      }
    }
  }
`;

export const SEARCH_OFFICES_QUERY = gql`
  ${OFFICE_FIELDS}
  ${OFFICE_INTRO_TEASER_IMAGE}
  ${OFFICE_LOCATION}
  query SearchOffices(
    $filter: OfficeModelFilter
    $orderBy: [OfficeModelOrderBy]
    $first: IntType
    $skip: IntType
    $locale: SiteLocale!
  ) {
    results: allOffices(
      filter: $filter
      orderBy: $orderBy
      first: $first
      skip: $skip
      locale: $locale
    ) {
      ...OfficeFields
      ...OfficeIntroTeaserImage
      officeLocations {
        ...OfficeLocation
      }
    }
    meta: _allOfficesMeta(filter: $filter) {
      count
    }
  }
`;

export const SEARCH_OFFICES_BY_GEOLOCATION_QUERY = gql`
  ${OFFICE_FIELDS}
  ${OFFICE_INTRO_TEASER_IMAGE}
  query SearchOfficesByGeolocation($filter: OfficeModelFilter, $locale: SiteLocale!) {
    results: allOffices(filter: $filter, orderBy: title_ASC, locale: $locale) {
      ...OfficeFields
      ...OfficeIntroTeaserImage
      introImage {
        id
        responsiveImage(
          imgixParams: { auto: format, fit: crop, crop: focalpoint, w: "500", h: "500", q: "75" }
        ) {
          alt
          height
          src
          title
          width
        }
      }
    }
  }
`;

export const SEARCH_ARTICLES_QUERY = gql`
  ${ARTICLE_FIELDS}
  ${ARTICLE_TEASER_IMAGE}
  query Search(
    $filter: ArticleModelFilter
    $orderBy: [ArticleModelOrderBy]
    $first: IntType
    $skip: IntType
    $locale: SiteLocale!
  ) {
    results: allArticles(
      filter: $filter
      orderBy: $orderBy
      first: $first
      skip: $skip
      locale: $locale
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
    meta: _allArticlesMeta(filter: $filter) {
      count
    }
  }
`;

export const SEARCH_ARTICLES_BY_TAG_QUERY = gql`
  ${ARTICLE_FIELDS}
  ${ARTICLE_TEASER_IMAGE}
  query SearchByTag(
    $primaryFilter: ArticleModelFilter
    $secondaryFilter: ArticleModelFilter
    $orderBy: [ArticleModelOrderBy]
    $first: IntType
    $skip: IntType
    $locale: SiteLocale!
  ) {
    primaryArticles: allArticles(
      filter: $primaryFilter
      orderBy: $orderBy
      first: $first
      skip: $skip
      locale: $locale
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
    secondaryArticles: allArticles(
      filter: $secondaryFilter
      orderBy: $orderBy
      first: $first
      skip: $skip
      locale: $locale
    ) {
      ...ArticleFields
      ...ArticleTeaserImage
    }
    primaryMeta: _allArticlesMeta(filter: $primaryFilter) {
      count
    }
    secondaryMeta: _allArticlesMeta(filter: $secondaryFilter) {
      count
    }
  }
`;

export const SEARCH_MUNICIPALITIES = gql`
  query SearchMunicipalities($term: String!, $locale: SiteLocale!) {
    results: allMunicipalities(
      filter: {
        OR: [
          { title: { matches: { pattern: $term } } }
          { searchTerms: { matches: { pattern: $term } } }
          { postalCode: { matches: { pattern: $term } } }
        ]
      }
      orderBy: title_ASC
      locale: $locale
    ) {
      title
      city
      postalCode
      searchTerms
      coordinates {
        latitude
        longitude
      }
    }
  }
`;
